import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiJson, apiUpload } from './api';
import { useSEO } from './useSEO';

/**
 * Admin Paper Manager
 *
 * Workflow:
 *   1. Pick level → subject → board → category (taxonomy).
 *   2. "Scan folder" lists every file under that taxonomy on disk.
 *   3. "Preview grouping" calls the bulk-import preview endpoint, which
 *      heuristically clusters files into Question / Markscheme / Model trios.
 *   4. Admin tweaks titles or role assignments inline, then "Import" creates
 *      the corresponding papers + paper_files rows pointing at on-disk files.
 *   5. The lower section lists existing papers for this taxonomy and lets
 *      the admin rename/delete them, swap individual files, or upload new ones.
 */

const ROLE_LABELS = {
  question: 'Question',
  markscheme: 'Markscheme',
  model: 'Model answer',
  solution_video: 'Solution video',
  formula_sheet: 'Formula sheet',
};
const PRIMARY_ROLES = ['question', 'markscheme', 'model'];

export default function AdminPapers() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState({ level: '', subject: '', board: '', category: '', groupLabel: '' });

  const [scanFiles, setScanFiles] = useState([]);
  const [previewGroups, setPreviewGroups] = useState([]);
  const [existingPapers, setExistingPapers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useSEO({
    title: 'Admin: paper manager',
    description: 'Organize PDFs into Question/Markscheme/Model groups for the interactive viewer.',
    canonicalPath: '/admin/papers',
  });

  // ── Redirect non-admins ────────────────────────────────────────────────
  useEffect(() => {
    if (user === null) {
      navigate('/login');
    } else if (user && !user.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  // ── Taxonomy loaders (re-using existing endpoints) ─────────────────────
  const loadLevels = useCallback(async () => {
    try { setLevels(await apiJson('/api/levels') || []); } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { if (user?.isAdmin) loadLevels(); }, [user, loadLevels]);

  useEffect(() => {
    if (!selected.level) return setSubjects([]);
    apiJson(`/api/subjects/${encodeURIComponent(selected.level)}`).then(setSubjects).catch((e) => setError(e.message));
  }, [selected.level]);

  useEffect(() => {
    if (!selected.level || !selected.subject) return setBoards([]);
    apiJson(`/api/boards/${encodeURIComponent(selected.level)}/${encodeURIComponent(selected.subject)}`).then(setBoards).catch((e) => setError(e.message));
  }, [selected.level, selected.subject]);

  useEffect(() => {
    if (!selected.level || !selected.subject || !selected.board) return setCategories([]);
    apiJson(`/api/resources/${encodeURIComponent(selected.level)}/${encodeURIComponent(selected.subject)}/${encodeURIComponent(selected.board)}`)
      .then((data) => setCategories(Object.keys(data || {})))
      .catch((e) => setError(e.message));
  }, [selected.level, selected.subject, selected.board]);

  const taxonomyComplete = selected.level && selected.subject && selected.board && selected.category;

  // ── Existing papers for this taxonomy ──────────────────────────────────
  const refreshExisting = useCallback(async () => {
    if (!taxonomyComplete) return setExistingPapers([]);
    try {
      const params = new URLSearchParams({
        level: selected.level, subject: selected.subject,
        board: selected.board, category: selected.category,
      });
      const data = await apiJson(`/api/papers?${params.toString()}`);
      setExistingPapers(data.papers || []);
    } catch (e) { setError(e.message); }
  }, [selected, taxonomyComplete]);

  useEffect(() => { refreshExisting(); }, [refreshExisting]);

  // ── Scan + preview ─────────────────────────────────────────────────────
  const doScan = async () => {
    if (!taxonomyComplete) return;
    setBusy(true); setError(''); setStatus('Scanning folder...');
    try {
      const params = new URLSearchParams({
        level: selected.level, subject: selected.subject,
        board: selected.board, category: selected.category,
      });
      const data = await apiJson(`/api/admin/papers/scan?${params.toString()}`, { token });
      setScanFiles(data.files || []);
      setStatus(`Found ${data.files?.length || 0} files`);
      setPreviewGroups([]);
    } catch (e) { setError(e.message); setStatus(''); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!scanFiles.length) return;
    setBusy(true); setError(''); setStatus('Grouping files...');
    try {
      const data = await apiJson('/api/admin/papers/bulk-import/preview', {
        method: 'POST', token,
        body: { files: scanFiles.map((f) => ({ name: f.name, relPath: f.relPath })) },
      });
      setPreviewGroups(data.groups || []);
      setStatus(`Grouped into ${data.groups?.length || 0} papers (review then import)`);
    } catch (e) { setError(e.message); setStatus(''); }
    finally { setBusy(false); }
  };

  const updateGroupTitle = (idx, title) => {
    setPreviewGroups((prev) => prev.map((g, i) => (i === idx ? { ...g, baseTitle: title } : g)));
  };
  const updateGroupRole = (idx, role, file) => {
    setPreviewGroups((prev) => prev.map((g, i) => {
      if (i !== idx) return g;
      const next = { ...g, files: { ...g.files } };
      if (file) next.files[role] = file; else delete next.files[role];
      return next;
    }));
  };
  const removeGroup = (idx) => setPreviewGroups((prev) => prev.filter((_, i) => i !== idx));

  const doCommit = async () => {
    if (!previewGroups.length) return;
    setBusy(true); setError(''); setStatus('Importing...');
    try {
      const data = await apiJson('/api/admin/papers/bulk-import/commit', {
        method: 'POST', token,
        body: {
          level: selected.level, subject: selected.subject, board: selected.board, category: selected.category,
          groupLabel: selected.groupLabel || null,
          groups: previewGroups,
        },
      });
      setStatus(`Imported ${data.created} papers`);
      setPreviewGroups([]);
      refreshExisting();
    } catch (e) { setError(e.message); setStatus(''); }
    finally { setBusy(false); }
  };

  // ── Existing-paper actions ─────────────────────────────────────────────
  const renamePaper = async (paper) => {
    const next = window.prompt('New title', paper.title);
    if (!next || next === paper.title) return;
    try {
      await apiJson(`/api/admin/papers/${paper.id}`, { method: 'PATCH', token, body: { title: next } });
      refreshExisting();
    } catch (e) { setError(e.message); }
  };
  const deletePaper = async (paper) => {
    if (!window.confirm(`Delete "${paper.title}"? (Files on disk are kept; this only removes the grouping.)`)) return;
    try {
      await apiJson(`/api/admin/papers/${paper.id}`, { method: 'DELETE', token });
      refreshExisting();
    } catch (e) { setError(e.message); }
  };
  const detachRole = async (paper, role) => {
    if (!window.confirm(`Remove the ${ROLE_LABELS[role]} link from "${paper.title}"?`)) return;
    try {
      await apiJson(`/api/admin/papers/${paper.id}/files/${role}`, { method: 'DELETE', token });
      refreshExisting();
    } catch (e) { setError(e.message); }
  };
  const uploadRoleFile = async (paper, role, file) => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('role', role);
      await apiUpload(`/api/admin/papers/${paper.id}/files`, fd, { token });
      setStatus(`Uploaded ${role} for "${paper.title}"`);
      refreshExisting();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  // ── Render helpers (must be declared before any early return) ─────────
  const filesGroupedBySubfolder = useMemo(() => {
    const map = new Map();
    for (const f of scanFiles) {
      const key = f.subfolder || '(root)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    return Array.from(map.entries());
  }, [scanFiles]);

  if (!user?.isAdmin) return null;

  return (
    <div style={{ maxWidth: 1100, margin: '24px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h1 style={{ margin: 0 }}>Paper Manager</h1>
        <Link to="/admin" className="nav-link">← Back to file uploads</Link>
      </div>
      <p style={{ color: 'var(--muted, #666)', marginTop: 4 }}>
        Group existing PDFs into Question / Markscheme / Model sets so they show up in the interactive viewer.
      </p>

      {error && <div style={errorBox}>{error}</div>}
      {status && <div style={statusBox}>{status}</div>}

      {/* Taxonomy picker */}
      <section style={card}>
        <h2 style={h2}>1. Choose location</h2>
        <div style={selectorRow}>
          <Picker label="Level" value={selected.level} options={levels}
            onChange={(v) => setSelected({ level: v, subject: '', board: '', category: '', groupLabel: '' })} />
          <Picker label="Subject" value={selected.subject} options={subjects} disabled={!selected.level}
            onChange={(v) => setSelected((p) => ({ ...p, subject: v, board: '', category: '' }))} />
          <Picker label="Board" value={selected.board} options={boards} disabled={!selected.subject}
            onChange={(v) => setSelected((p) => ({ ...p, board: v, category: '' }))} />
          <Picker label="Category" value={selected.category} options={categories} disabled={!selected.board}
            onChange={(v) => setSelected((p) => ({ ...p, category: v }))} />
          <label style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
            <span style={lbl}>Group label (optional)</span>
            <input style={input} type="text" placeholder="e.g. 2023" value={selected.groupLabel}
              onChange={(e) => setSelected((p) => ({ ...p, groupLabel: e.target.value }))} />
          </label>
        </div>
      </section>

      {/* Bulk import */}
      <section style={card}>
        <h2 style={h2}>2. Bulk import from existing files</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={btn} disabled={!taxonomyComplete || busy} onClick={doScan}>Scan folder</button>
          <button style={btn} disabled={!scanFiles.length || busy} onClick={doPreview}>Auto-group files</button>
          <button style={btnPrimary} disabled={!previewGroups.length || busy} onClick={doCommit}>
            Import {previewGroups.length || ''} paper{previewGroups.length === 1 ? '' : 's'}
          </button>
        </div>

        {!previewGroups.length && scanFiles.length > 0 && (
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer' }}>Scanned files ({scanFiles.length})</summary>
            <div style={{ marginTop: 8, maxHeight: 280, overflowY: 'auto', fontSize: 13 }}>
              {filesGroupedBySubfolder.map(([sub, items]) => (
                <div key={sub} style={{ marginBottom: 8 }}>
                  <strong>{sub}</strong>
                  <ul style={{ margin: '4px 0 0 16px' }}>
                    {items.map((f) => <li key={f.relPath}>{f.name}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        )}

        {previewGroups.length > 0 && (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {previewGroups.map((g, idx) => (
              <PreviewGroupCard
                key={idx} group={g} idx={idx} allFiles={scanFiles}
                onTitle={(v) => updateGroupTitle(idx, v)}
                onRole={(role, file) => updateGroupRole(idx, role, file)}
                onRemove={() => removeGroup(idx)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Existing papers */}
      <section style={card}>
        <h2 style={h2}>3. Existing papers ({existingPapers.length})</h2>
        {!taxonomyComplete && <p style={{ color: 'var(--muted-2)' }}>Pick a category to see existing papers.</p>}
        {taxonomyComplete && existingPapers.length === 0 && (
          <p style={{ color: 'var(--muted-2)' }}>None yet. Use the bulk importer above.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {existingPapers.map((paper) => (
            <ExistingPaperRow
              key={paper.id} paper={paper}
              onRename={() => renamePaper(paper)}
              onDelete={() => deletePaper(paper)}
              onDetach={(role) => detachRole(paper, role)}
              onUpload={(role, file) => uploadRoleFile(paper, role, file)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

function Picker({ label, value, options, onChange, disabled }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
      <span style={lbl}>{label}</span>
      <select style={input} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">— select —</option>
        {(options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </label>
  );
}

function PreviewGroupCard({ group, idx, allFiles, onTitle, onRole, onRemove }) {
  return (
    <div style={{ border: '1px solid var(--chip-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <input style={{ ...input, flex: 1 }} value={group.baseTitle || ''}
          onChange={(e) => onTitle(e.target.value)} placeholder="Paper title" />
        <button style={btnDanger} onClick={onRemove}>Remove</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
        {PRIMARY_ROLES.map((role) => {
          const current = group.files?.[role];
          return (
            <div key={role} style={{ background: 'var(--surface-soft)', padding: 8, borderRadius: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{ROLE_LABELS[role]}</div>
              <select style={{ ...input, marginTop: 4 }}
                value={current?.relPath || ''}
                onChange={(e) => {
                  const file = allFiles.find((f) => f.relPath === e.target.value);
                  onRole(role, file ? { relPath: file.relPath, name: file.name } : null);
                }}>
                <option value="">— none —</option>
                {allFiles.map((f) => <option key={f.relPath} value={f.relPath}>{f.name}</option>)}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExistingPaperRow({ paper, onRename, onDelete, onDetach, onUpload }) {
  return (
    <div style={{ border: '1px solid var(--chip-border)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600 }}>{paper.title}</div>
          {paper.groupLabel && <div style={{ fontSize: 12, color: 'var(--muted-2)' }}>{paper.groupLabel}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Link to={`/viewer/${paper.id}`} style={{ ...btn, textDecoration: 'none' }}>Open viewer</Link>
          <button style={btn} onClick={onRename}>Rename</button>
          <button style={btnDanger} onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
        {PRIMARY_ROLES.map((role) => {
          const file = paper.files?.[role];
          const inputId = `up-${paper.id}-${role}`;
          return (
            <div key={role} style={{ background: 'var(--surface-soft)', padding: 8, borderRadius: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{ROLE_LABELS[role]}</div>
              {file ? (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <a href={file.url} target="_blank" rel="noreferrer">{file.filePath.split('/').pop()}</a>
                  <button style={{ ...btnLink, marginLeft: 8 }} onClick={() => onDetach(role)}>remove</button>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 4 }}>not set</div>
              )}
              <label htmlFor={inputId} style={{ ...btnLink, display: 'inline-block', marginTop: 4, cursor: 'pointer' }}>
                {file ? 'Replace…' : 'Upload…'}
              </label>
              <input id={inputId} type="file" style={{ display: 'none' }}
                onChange={(e) => { onUpload(role, e.target.files?.[0]); e.target.value = ''; }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Inline styles (kept local — App.css doesn't have a design system for forms)
// ────────────────────────────────────────────────────────────────────────
const card = { border: '1px solid var(--chip-border)', borderRadius: 10, padding: 16, marginTop: 16, background: 'var(--surface)', color: 'var(--text)' };
const h2 = { margin: '0 0 12px', fontSize: 18 };
const selectorRow = { display: 'flex', flexWrap: 'wrap', gap: 12 };
const lbl = { fontSize: 12, color: 'var(--muted)', marginBottom: 2 };
const input = { padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 6, fontSize: 14, background: 'var(--surface)', color: 'var(--text)' };
const btn = { padding: '6px 12px', border: '1px solid var(--chip-border)', borderRadius: 6, background: 'var(--surface-soft)', color: 'var(--text)', cursor: 'pointer', fontSize: 13 };
const btnPrimary = { ...btn, background: 'var(--link)', color: '#fff', borderColor: 'var(--link)' };
const btnDanger = { ...btn, background: 'var(--surface)', color: '#dc2626', borderColor: 'rgba(220,38,38,0.45)' };
const btnLink = { background: 'transparent', border: 'none', color: 'var(--link)', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' };
const errorBox = { background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.45)', color: '#fca5a5', padding: 8, borderRadius: 6, marginTop: 12 };
const statusBox = { background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.45)', color: 'var(--link)', padding: 8, borderRadius: 6, marginTop: 12 };
